/* eslint-disable react/prop-types */
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import cx from "classnames";
import moment from "moment/moment";
import Tooltip from "@material-ui/core/Tooltip";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import {
  CloudUploadRounded,
  Done,
  GetApp,
  HighlightOff,
  LinkRounded,
  PlaylistAddCheckRounded,
  Timer,
  Cached,
  Notes,
  AssignmentTurnedInRounded,
} from "@material-ui/icons";
import Button from "../../../components/CustomButtons/Button";
import CustomLinearProgress from "../../../components/CustomLinearProgress/CustomLinearProgress";
import { store } from "../../../store";
import { getAcceptExtensions } from "./utils/fileTypes";
import InputDrawerDownloadPopper from "./InputDrawerDownloadPopper";
import InputDrawerReprocessButton from "./InputDrawerReprocessButton";

export default function InputDrawerStatusPanel({
  input,
  selectedMonthData,
  userDetails,
  specialOptionsApi,
  classes,
  isUploading,
  fileInputRef,
  drawerUi,
  drawerActions,
}) {
  const {
    replicatingFile,
    fileName,
    wrongDataType,
    isDragging,
    downloadButtonPopperOpen,
    downloadInProgress,
  } = drawerUi;
  const {
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    onFileSelect,
    onUploadFileClick,
    onReplicateFileClick,
    onDownloadLastUploadedFileButton,
    onRetryProcessingButtonClick,
    onViewNotesClick,
    onClickAwayDownloadFileSelection,
    onDownloadFileSelection,
  } = drawerActions;

  const { t } = useTranslation();
  const fileUploadProgress = useSelector(
    () => store.getState().file_transfer.progress,
  );
  const uploadingText = useMemo(() => {
    return replicatingFile
      ? t("modeling_inputs_v2.replicating")
      : fileUploadProgress < 100
        ? t("modeling_inputs_v2.uploading")
        : t("modeling_inputs_v2.please_wait");
  }, [replicatingFile, fileUploadProgress, t]);

  if (!input) return null;

  const dropZoneClass = cx(
    classes.inputDrawerFileDropZone,
    isDragging
      ? classes.inputDrawerFileDropZoneDragging
      : wrongDataType
        ? classes.inputDrawerFileDropZoneWrong
        : fileName
          ? classes.inputDrawerFileDropZoneHasFile
          : classes.inputDrawerFileDropZoneEmpty,
  );

  const reprocessDisabled =
    isUploading ||
    (input.restricted && userDetails.restricted_data_privilege) ||
    input.stages.data_source !== "success" ||
    specialOptionsApi.isMonthInBatchRange(selectedMonthData);

  return (
    <div className={classes.inputDrawerStatusRoot}>
      {selectedMonthData.status === "complete" && (
        <div className={classes.inputDrawerMonthCompleteBanner}>
          {t("modeling_inputs_v2.month_modeling_finished")}
        </div>
      )}
      <TableContainer>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell
                align={"center"}
                className={classes.inputDrawerTableCellIcon}
              >
                {input.stages.data_source === "waiting" && (
                  <Timer
                    className={cx({
                      [classes.inputStatusIcon]: true,
                    })}
                  />
                )}
                {input.stages.data_source !== "waiting" && (
                  <Done
                    className={cx({
                      [classes.inputStatusIcon]: true,
                    })}
                  />
                )}
              </TableCell>
              <TableCell className={classes.inputDrawerTableCellStage}>
                <Tooltip
                  title={t("modeling_inputs_v2.help.source." + input.source)}
                  placement={"left"}
                  arrow
                  PopperProps={{
                    style: { marginRight: "-6px" },
                  }}
                  classes={{ tooltip: classes.largeTooltip }}
                >
                  <div className={classes.inputDrawerTooltipHelpRow}>
                    <div
                      className={cx({
                        [classes.inputStatusItem]: true,
                        [input.stages.data_source]: true,
                      })}
                    >
                      {input.source === "file_upload" && (
                        <CloudUploadRounded
                          className={classes.inputDrawerStageIcon}
                        />
                      )}
                      {input.source === "auto_generated" && (
                        <PlaylistAddCheckRounded
                          className={classes.inputDrawerStageIcon}
                        />
                      )}
                      {t(
                        "modeling_inputs_v2.stages.data_source." + input.source,
                      )}
                    </div>
                  </div>
                </Tooltip>
              </TableCell>
              {input.source === "file_upload" && (
                <TableCell className={classes.inputDrawerTableCellFileActions}>
                  {input.updated_by_username && (
                    <div className={classes.inputDrawerSentByWrap}>
                      <div className={classes.inputDrawerSentByBox}>
                        <div>
                          {t("modeling_inputs_v2.sent_by") +
                            " " +
                            input.updated_by_username +
                            " " +
                            t("modeling_inputs_v2.in") +
                            " " +
                            moment
                              .utc(input.updated_in, "YYYY-MM-DD")
                              .format("l") +
                            " " +
                            t("modeling_inputs_v2.with") +
                            " " +
                            (input.data_from
                              ? input.data_from[0] ===
                                  selectedMonthData.month &&
                                input.data_from[1] === selectedMonthData.year
                                ? t("modeling_inputs_v2.new_data")
                                : t("modeling_inputs_v2.data_from") +
                                  " " +
                                  input.data_from[0] +
                                  "/" +
                                  input.data_from[1]
                              : t("modeling_inputs_v2.new_data"))}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className={classes.inputDrawerSendNewFileLabel}>
                    {t("modeling_inputs_v2.send_new_file")}
                  </div>
                  <div
                    className={dropZoneClass}
                    onDragEnter={onDragEnter}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept={getAcceptExtensions(input.expected_file_type)}
                      className={classes.inputDrawerHiddenFileInput}
                      onChange={(e) => {
                        if (e.target.files[0]) onFileSelect(e.target.files[0]);
                      }}
                    />
                    <div
                      className={cx(classes.inputDrawerFileNameOverlay, {
                        [classes.inputDrawerFileNameOverlayNoPointer]:
                          isDragging,
                      })}
                    >
                      {fileName ||
                        "[ " + t("modeling_inputs_v2.no_file_selected") + " ]"}
                    </div>
                    <div
                      className={cx(classes.inputDrawerFileHintOverlay, {
                        [classes.inputDrawerFileHintOverlayNoPointer]:
                          isDragging,
                      })}
                    >
                      <span
                        className={cx(
                          classes.inputDrawerFileLink,
                          classes.inputDrawerFileLinkThemed,
                          {
                            [classes.inputDrawerFileLinkNoPointer]: isDragging,
                          },
                        )}
                        onClick={() => fileInputRef.current.click()}
                      >
                        {t("modeling_inputs_v2.choose_a_file")}
                      </span>
                      {" " + t("modeling_inputs_v2.or_drag_and_drop_here")}
                    </div>
                    {wrongDataType && (
                      <div
                        className={cx(classes.inputDrawerWrongTypeBadge, {
                          [classes.inputDrawerWrongTypeBadgeNoPointer]:
                            isDragging,
                        })}
                      >
                        {t("modeling_inputs_v2.wrong_file_type")}
                      </div>
                    )}
                    <div
                      className={cx(classes.inputDrawerSendIconWrap, {
                        [classes.inputDrawerSendIconWrapNoPointer]: isDragging,
                        [classes.attention]:
                          !wrongDataType && fileName !== "" && !isUploading,
                      })}
                    >
                      <Tooltip title={t("modeling_inputs_v2.send")}>
                        <span>
                          <Button
                            simple={wrongDataType || fileName === ""}
                            justIcon
                            color={"client"}
                            onClick={() => onUploadFileClick()}
                            disabled={
                              isUploading ||
                              wrongDataType ||
                              fileName === "" ||
                              specialOptionsApi.isMonthInBatchRange(
                                selectedMonthData,
                              )
                            }
                          >
                            <CloudUploadRounded
                              className={classes.inputDrawerCloudUploadIcon}
                            />
                          </Button>
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                  <div className={classes.inputDrawerReplicateRow}>
                    <span className={classes.inputDrawerOrSpacing}>
                      {t("modeling_inputs_v2.or")}
                    </span>
                    <Button
                      color={"system_gray"}
                      size={"sm"}
                      className={classes.inputDrawerReplicateButton}
                      onClick={() => onReplicateFileClick()}
                      disabled={
                        isUploading ||
                        !input.available_data_from ||
                        specialOptionsApi.isMonthInBatchRange(selectedMonthData)
                      }
                    >
                      <Cached className={classes.inputDrawerSmallIcon} />
                      {t("modeling_inputs_v2.use_previous_file") +
                        " (" +
                        (input.available_data_from
                          ? t(
                              "months." +
                                input.available_data_from[0].toString(),
                            ) +
                            "-" +
                            input.available_data_from[1].toString().slice(-2)
                          : t("modeling_inputs_v2.not_available")) +
                        ")"}
                    </Button>
                  </div>
                  <div
                    className={cx({
                      [classes.uploadProgressBar]: true,
                      ["show"]: isUploading,
                    })}
                  >
                    <CustomLinearProgress
                      style={{ margin: "16px 8px 2px", height: "6px" }}
                      variant={
                        replicatingFile ? "indeterminate" : "determinate"
                      }
                      color="client"
                      value={fileUploadProgress}
                      rootClasses={classes.customProgressBarRoot}
                      barClasses={classes.customProgressBarBar}
                    />
                    <div className={classes.inputDrawerUploadingLabel}>
                      {uploadingText}
                    </div>
                  </div>
                  <div className={classes.inputDrawerDownloadActionsRow}>
                    <Button
                      color={"system_gray"}
                      size={"sm"}
                      className={classes.inputDrawerDownloadButton}
                      disabled={
                        isUploading ||
                        (input.restricted &&
                          userDetails.restricted_data_privilege) ||
                        input.stages.data_source !== "success"
                      }
                      onClick={onDownloadLastUploadedFileButton}
                      aria-owns={
                        downloadButtonPopperOpen
                          ? "download-file-menu-list"
                          : null
                      }
                      aria-haspopup={"true"}
                    >
                      <GetApp className={classes.inputDrawerGetAppIcon} />
                      {t("modeling_inputs_v2.download_last_file_sent")}
                    </Button>
                    {userDetails.user_role === "DEV" && (
                      <InputDrawerReprocessButton
                        classes={classes}
                        t={t}
                        disabled={reprocessDisabled}
                        onRetryProcessingButtonClick={
                          onRetryProcessingButtonClick
                        }
                        downloadButtonPopperOpen={downloadButtonPopperOpen}
                      />
                    )}
                  </div>
                  <InputDrawerDownloadPopper
                    classes={classes}
                    t={t}
                    downloadButtonPopperOpen={downloadButtonPopperOpen}
                    downloadInProgress={downloadInProgress}
                    onClickAwayDownloadFileSelection={
                      onClickAwayDownloadFileSelection
                    }
                    onDownloadFileSelection={onDownloadFileSelection}
                    cleaned_up_file={["success", "warning"].includes(
                      input.stages.data_structure,
                    )}
                    processed_file_available={input.processed_file_available}
                  />
                </TableCell>
              )}
              {input.source === "auto_generated" && (
                <TableCell className={classes.inputDrawerTableCellFileActions}>
                  <div className={classes.inputDrawerAutoGenDownloadWrap}>
                    <Button
                      color={"system_gray"}
                      size={"sm"}
                      className={classes.inputDrawerDownloadButton}
                      disabled={
                        isUploading ||
                        (input.restricted &&
                          userDetails.restricted_data_privilege) ||
                        input.stages.data_source !== "success"
                      }
                      onClick={onDownloadLastUploadedFileButton}
                      aria-owns={
                        downloadButtonPopperOpen
                          ? "download-file-menu-list"
                          : null
                      }
                      aria-haspopup={"true"}
                    >
                      <GetApp className={classes.inputDrawerGetAppIcon} />
                      {t("modeling_inputs_v2.download_generated_file")}
                    </Button>
                    {userDetails.user_role === "DEV" && (
                      <InputDrawerReprocessButton
                        classes={classes}
                        t={t}
                        disabled={reprocessDisabled}
                        onRetryProcessingButtonClick={
                          onRetryProcessingButtonClick
                        }
                        downloadButtonPopperOpen={downloadButtonPopperOpen}
                      />
                    )}
                  </div>
                  <InputDrawerDownloadPopper
                    classes={classes}
                    t={t}
                    downloadButtonPopperOpen={downloadButtonPopperOpen}
                    downloadInProgress={downloadInProgress}
                    onClickAwayDownloadFileSelection={
                      onClickAwayDownloadFileSelection
                    }
                    onDownloadFileSelection={onDownloadFileSelection}
                    cleaned_up_file={false}
                    processed_file_available={true}
                  />
                </TableCell>
              )}
            </TableRow>
            <TableRow>
              <TableCell
                align={"center"}
                className={classes.inputDrawerTableCellIcon}
              >
                {input.stages.data_structure === "waiting" && (
                  <Timer
                    className={cx({
                      [classes.inputStatusIcon]: true,
                      ["waiting"]: true,
                    })}
                  />
                )}
                {["success", "warning"].includes(
                  input.stages.data_structure,
                ) && (
                  <Done
                    className={cx({
                      [classes.inputStatusIcon]: true,
                    })}
                  />
                )}
                {["error", "system_error"].includes(
                  input.stages.data_structure,
                ) && (
                  <HighlightOff
                    className={cx({
                      [classes.inputStatusIcon]: true,
                    })}
                  />
                )}
              </TableCell>
              <TableCell className={classes.inputDrawerTableCellStage}>
                <Tooltip
                  title={t("modeling_inputs_v2.help.data_structure")}
                  placement={"left"}
                  arrow
                  PopperProps={{
                    style: { marginRight: "-6px" },
                  }}
                  classes={{ tooltip: classes.largeTooltip }}
                >
                  <div className={classes.inputDrawerTooltipHelpRow}>
                    <div
                      className={cx({
                        [classes.inputStatusItem]: true,
                        [input.stages.data_structure]: true,
                        ["not-next"]: input.stages.data_structure === "waiting",
                      })}
                    >
                      <AssignmentTurnedInRounded
                        className={classes.inputDrawerStageIcon}
                      />
                      {t("modeling_inputs_v2.stages.data_structure")}
                    </div>
                  </div>
                </Tooltip>
              </TableCell>
              <TableCell rowSpan={2}>
                <div className={classes.inputDrawerNotesCellWrap}>
                  <Button
                    color={
                      ["error", "system_error"].includes(
                        input.stages.data_structure,
                      ) ||
                      ["error", "system_error"].includes(
                        input.stages.cross_linking,
                      )
                        ? "danger"
                        : input.stages.data_structure === "warning" ||
                            input.stages.cross_linking === "warning"
                          ? "warning"
                          : "system_gray"
                    }
                    size={"sm"}
                    className={classes.inputDrawerNotesButton}
                    onClick={() => onViewNotesClick()}
                    disabled={
                      isUploading || input.stages.data_source !== "success"
                    }
                  >
                    <Notes className={classes.inputDrawerSmallIcon} />
                    {t("modeling_inputs_v2.last_update_notes")}
                  </Button>
                </div>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                align={"center"}
                className={classes.inputDrawerTableCellIcon}
              >
                {input.stages.cross_linking === "waiting" && (
                  <Timer
                    className={cx({
                      [classes.inputStatusIcon]: true,
                      ["waiting"]: !["success", "warning"].includes(
                        input.stages.data_structure,
                      ),
                    })}
                  />
                )}
                {["success", "warning"].includes(
                  input.stages.cross_linking,
                ) && (
                  <Done
                    className={cx({
                      [classes.inputStatusIcon]: true,
                    })}
                  />
                )}
                {["error", "system_error"].includes(
                  input.stages.cross_linking,
                ) && (
                  <HighlightOff
                    className={cx({
                      [classes.inputStatusIcon]: true,
                    })}
                  />
                )}
              </TableCell>
              <TableCell className={classes.inputDrawerTableCellStage}>
                <Tooltip
                  title={t("modeling_inputs_v2.help.data_cross_linking")}
                  placement={"left"}
                  arrow
                  PopperProps={{
                    style: { marginRight: "-6px" },
                  }}
                  classes={{ tooltip: classes.largeTooltip }}
                >
                  <div className={classes.inputDrawerTooltipHelpRow}>
                    <div
                      className={cx({
                        [classes.inputStatusItem]: true,
                        [input.stages.cross_linking]: true,
                        ["not-next"]: !["success", "warning"].includes(
                          input.stages.data_structure,
                        ),
                      })}
                    >
                      <LinkRounded className={classes.inputDrawerStageIcon} />
                      {t("modeling_inputs_v2.stages.data_cross_linking")}
                    </div>
                  </div>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
